import { useCallback, useState } from 'react'

import { useAtomValue, useSetAtom } from 'jotai'
import { match } from 'ts-pattern'

import {
  IconCheckmark,
  IconChevronDown,
  Button,
  SequenceMenu,
  useNotifications,
  IconPaid,
  IconPartiallyPaid,
  IconUncollectible,
  IconUnpaid
} from '@sequencehq/core-components'

import { useMutation } from '@sequencehq/api'
import { dashboard20240730Client } from '@sequencehq/api/dashboard/v20240730'

import {
  clientInvoiceAtom,
  isSavePendingAtom,
  invoiceIdAtom,
  invoicePaymentStatusAtom
} from 'Invoices/atoms/invoiceEditing.atom'
import { ProtectedButton, usePermissions } from 'lib/rbac'
import { Dashboardv99990101Api } from '@sequencehq/api/dashboard/v99990101'
import { EligibilityVerdict } from '../eligibility'
import { RecordPaymentDrawer, RecordFullPaymentModal } from './RecordPayment'
import {
  getInvoiceBalanceDetails,
  isPositiveAmount
} from './RecordPayment/recordPayment.helpers'

type InvoicePaymentStatus = Dashboardv99990101Api.Invoices.InvoicePaymentStatus

const ORDERED_STATUSES: InvoicePaymentStatus[] = [
  'UNPAID',
  'PAID',
  'PARTIALLY_PAID',
  'UNCOLLECTIBLE'
]

// Which statuses can be selected from each current status (manual payments on).
// PAID is terminal — the balance is cleared, so the select is disabled. From
// PARTIALLY_PAID, re-selecting it records another partial payment.
//
//   UNPAID          -> PARTIALLY_PAID, PAID, UNCOLLECTIBLE
//   PARTIALLY_PAID  -> PARTIALLY_PAID, PAID, UNCOLLECTIBLE
//   PAID            -> (none)
//   UNCOLLECTIBLE   -> UNPAID, PARTIALLY_PAID, PAID
const ALLOWED_TRANSITIONS: Record<
  InvoicePaymentStatus,
  InvoicePaymentStatus[]
> = {
  UNPAID: ['PARTIALLY_PAID', 'PAID', 'UNCOLLECTIBLE'],
  PARTIALLY_PAID: ['PARTIALLY_PAID', 'PAID', 'UNCOLLECTIBLE'],
  PAID: [],
  UNCOLLECTIBLE: ['UNPAID', 'PARTIALLY_PAID', 'PAID']
}

// Marking paid/partially-paid records an actual payment (which moves the status
// server-side) rather than forcing the status flag. 'confirmFull' clears the
// balance; 'drawer' lets the user enter a partial amount.
type ActiveFlow = 'none' | 'drawer' | 'confirmFull'

// Statuses that record an actual payment (rather than a plain status flag) when
// manual payments are on — so they require an outstanding balance to collect.
const PAYMENT_STATUSES: InvoicePaymentStatus[] = ['PAID', 'PARTIALLY_PAID']

export const DropdownUpdatePaymentStatus = ({
  manualPaymentEligibility
}: {
  manualPaymentEligibility: EligibilityVerdict
}) => {
  const isEligible = manualPaymentEligibility === 'eligible'
  const isEligibilityPending = manualPaymentEligibility === 'pending'
  const paymentStatus = useAtomValue(invoicePaymentStatusAtom)
  const invoiceId = useAtomValue(invoiceIdAtom)
  const clientInvoice = useAtomValue(clientInvoiceAtom)
  const isSavePending = useAtomValue(isSavePendingAtom)
  const { can } = usePermissions()
  const [activeFlow, setActiveFlow] = useState<ActiveFlow>('none')
  const updatePaymentStatus = useUpdatePaymentStatus()

  if (!paymentStatus) return null

  // When eligible, a fully paid invoice is terminal — no status left to move
  // to, so the whole select is disabled.
  const allowedTargets = ALLOWED_TRANSITIONS[paymentStatus]
  const isTerminal = isEligible && allowedTargets.length === 0

  // Paid/Partially-paid record a payment, so they need something to collect;
  // without a positive balance those options would open a confirm/drawer whose
  // submit is stuck disabled, so gate them out instead.
  const canRecordPayment = clientInvoice
    ? isPositiveAmount(getInvoiceBalanceDetails(clientInvoice).balance)
    : false

  // Must be a Button element (not a wrapping component): SequenceMenu attaches
  // its ref/onClick via cloneElement, which a wrapper would swallow.
  const statusButton = (
    <Button
      variant="secondary"
      leadingIcon={<Icon paymentStatus={paymentStatus} />}
      trailingIcon={<IconChevronDown color="inherit" />}
      disabled={isSavePending || isTerminal || isEligibilityPending}
      data-testid="header.button.dropdown-update-payment-status"
    >
      {getPaymentStatusLabel(paymentStatus)}
    </Button>
  )

  if (!can('org.invoice:update')) {
    return (
      <ProtectedButton permission={'org.invoice:update'}>
        {statusButton}
      </ProtectedButton>
    )
  }

  // When eligible, "Paid"/"Partially paid" trigger the apply-payment flow;
  // otherwise every status is a direct flag update.
  const onSelect = (status: InvoicePaymentStatus) => {
    if (isEligible && status === 'PAID') {
      setActiveFlow('confirmFull')
      return
    }

    if (isEligible && status === 'PARTIALLY_PAID') {
      setActiveFlow('drawer')
      return
    }

    updatePaymentStatus(status)
  }

  return (
    <>
      {/* isTerminal is enforced by statusButton's own `disabled`, which blocks
          the click that opens the menu — SequenceMenu's `disabled` prop only
          applies to its fallback button, not a custom menuButton. */}
      <SequenceMenu
        menuButton={statusButton}
        alignment="bottom-right"
        items={ORDERED_STATUSES.map(status => ({ setIsOpen }) => (
          <PaymentStatusMenuItem
            paymentStatus={status}
            currentStatus={paymentStatus}
            // Ineligible keeps the legacy free-for-all. Eligible limits to the
            // allowed transitions, and drops payment-recording options when
            // there's no balance to collect (avoids a stuck confirm/drawer).
            isDisabled={
              isEligible &&
              (!allowedTargets.includes(status) ||
                (PAYMENT_STATUSES.includes(status) && !canRecordPayment))
            }
            onClick={() => {
              setIsOpen?.(false)
              onSelect(status)
            }}
          />
        ))}
      />
      {activeFlow === 'drawer' && invoiceId && (
        <RecordPaymentDrawer
          invoiceId={invoiceId}
          onClose={() => setActiveFlow('none')}
        />
      )}
      {activeFlow === 'confirmFull' && invoiceId && (
        <RecordFullPaymentModal
          invoiceId={invoiceId}
          onClose={() => setActiveFlow('none')}
        />
      )}
    </>
  )
}

// Records a direct status-flag update. Used for statuses that don't record a
// payment (Not paid, Uncollectible), and for every status when the invoice is
// ineligible for manual payments.
const useUpdatePaymentStatus = () => {
  const { displayPromiseNotification } = useNotifications()
  const invoiceId = useAtomValue(invoiceIdAtom)
  const partialUpdateInvoice = useSetAtom(clientInvoiceAtom)

  const { mutateAsync } = useMutation(
    dashboard20240730Client.putInvoicePaymentStatus
  )

  return useCallback(
    (paymentStatus: InvoicePaymentStatus) => {
      if (!invoiceId) return

      partialUpdateInvoice({ paymentStatus })

      displayPromiseNotification(mutateAsync({ invoiceId, paymentStatus }), {
        loading: 'Updating payment status...',
        success: 'Payment status updated',
        error: 'Failed to update payment status'
      })
    },
    [displayPromiseNotification, invoiceId, mutateAsync, partialUpdateInvoice]
  )
}

const PaymentStatusMenuItem = ({
  paymentStatus,
  currentStatus,
  isDisabled,
  onClick
}: {
  paymentStatus: InvoicePaymentStatus
  currentStatus: InvoicePaymentStatus
  isDisabled: boolean
  onClick: () => void
}) => (
  <SequenceMenu.Item
    uuid={paymentStatus}
    label={getPaymentStatusLabel(paymentStatus)}
    iconLeft={<Icon paymentStatus={paymentStatus} />}
    iconRight={
      currentStatus === paymentStatus && <IconCheckmark color="inherit" />
    }
    isDisabled={isDisabled}
    onClick={onClick}
    data-testid={`payment-status-menu-item.${paymentStatus.toLowerCase()}`}
  />
)

const Icon = ({ paymentStatus }: { paymentStatus: InvoicePaymentStatus }) => {
  return match(paymentStatus)
    .with('UNPAID', () => <IconUnpaid />)
    .with('PAID', () => <IconPaid />)
    .with('PARTIALLY_PAID', () => <IconPartiallyPaid />)
    .with('UNCOLLECTIBLE', () => <IconUncollectible />)
    .exhaustive()
}

const getPaymentStatusLabel = (paymentStatus: InvoicePaymentStatus): string => {
  return match(paymentStatus)
    .with('UNPAID', () => 'Not paid')
    .with('PAID', () => 'Paid')
    .with('PARTIALLY_PAID', () => 'Partially paid')
    .with('UNCOLLECTIBLE', () => 'Uncollectible')
    .exhaustive()
}
